import { attachRequestPage } from '../api/requestPage'
import { installCssFailureDiagnostics } from './cssFailureDiagnostics'

let activeReport

export function reportModuleLoadError(error, targetPath) {
  const target = String(targetPath || '').split(/[?#]/)[0].slice(0, 255)
  activeReport?.(error, 'javascript', `module_load target=${target}`, error)
}

// operation에는 사용자 입력 대신 고정된 처리 이름만 전달한다.
export function reportHandledBrowserError (error, operation) {
  activeReport?.(new Error(operation), 'javascript', operation, error)
}

export function reportBrowserTransportError (error) {
  if (!error?.request || error.response || error.code === 'ERR_CANCELED'
      || error.name === 'AbortError' || error.__CANCEL__) return
  try {
    const config = error.config || {}
    const url = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i.test(config.url || '')
      ? new URL(config.url, window.location.origin)
      : new URL(`${(config.baseURL || '').replace(/\/$/, '')}/${(config.url || '').replace(/^\//, '')}`, window.location.origin)
    if (url.origin !== window.location.origin || !/^\/(api|adminApi)\//.test(url.pathname)
        || /\/(browser-errors|internal\/error-events)(?:\/|$)/.test(url.pathname)) return
    reportHandledBrowserError(error, 'api_transport')
  } catch (_) { /* 잘못된 요청 URL은 통신 실패로 분류하지 않는다. */ }
}

// 독립 빌드하는 공개/관리자 SPA에서 동일한 전송 계약을 사용한다.
function clean (value, max = 4000) {
  return String(value || '')
    .replace(/(https?:\/\/[^\s?#]+)[?#][^\s)]*/g, '$1')
    .replace(/\b(Bearer)\s+\S+/gi, '$1 [숨김]')
    .replace(/((?:password|passwd|token|secret|authorization)\s*[:=]\s*)[^\s,;]+/gi, '$1[숨김]')
    .slice(0, max)
}

export function installBrowserErrors (app, source, { send = (...args) => window.fetch(...args) } = {}) {
  const releaseVersion = import.meta.env.VITE_RELEASE_VERSION || ''
  const previous = app.config.errorHandler
  const recent = new Map()
  const handled = new WeakSet()
  const pendingCss = []
  let disposed = false
  const deliver = (options, retryOnOnline = false) => {
    const failed = () => {
      // 전송 예외는 메모리에 보관하고 online 이벤트에서 한 번 재전송한다.
      if (!retryOnOnline || disposed) return
      const item = { options, at: Date.now() }
      item.timer = setTimeout(() => {
        const index = pendingCss.indexOf(item)
        if (index >= 0) pendingCss.splice(index, 1)
      }, 600000)
      pendingCss.push(item)
      if (pendingCss.length > 10) clearTimeout(pendingCss.shift().timer)
    }
    try {
      Promise.resolve(send('/api/v1/browser-errors', options)).catch(failed)
    } catch (_) { failed() }
  }
  const onOnline = () => {
    pendingCss.splice(0).forEach(item => {
      clearTimeout(item.timer)
      if (Date.now() - item.at < 600000) deliver(item.options)
    })
  }
  window.addEventListener('online', onOnline)
  let windowStart = Date.now()
  let count = 0
  const transmit = (error, kind, info = '', original, cssDiagnostic, observedRoute) => {
    try {
      const identity = original || error
      if (identity && typeof identity === 'object' && handled.has(identity)) return
      if (original && typeof original === 'object') handled.add(original)
      const now = Date.now()
      if (now - windowStart >= 60000) { count = 0; windowStart = now; recent.clear() }
      const message = clean(error?.message || (typeof error === 'string' ? error : '브라우저 실행 오류'), 1000)
      const stack = clean(error?.stack, 5000)
      const route = observedRoute ?? window.location.pathname.slice(0, 255)
      const key = route + '\n' + message + stack
      if (count >= 20 || (recent.has(key) && now - recent.get(key) < 10000)) return
      recent.set(key, now)
      count++
      const body = JSON.stringify({
        source, kind, message, stack, releaseVersion,
        route, info: clean(info, 500), ...(cssDiagnostic ? { cssDiagnostic } : {})
      })
      const headers = attachRequestPage({ headers: { 'Content-Type': 'application/json' } }).headers
      // 100ms 수집 중 화면이 이동해도 실패 시점의 화면 경로를 유지한다.
      if (cssDiagnostic) headers['X-Client-Page'] = encodeURIComponent(route)
      // 인증 토큰/쿠키/요청 본문을 전송하지 않고 전송 실패도 다시 보고하지 않는다.
      deliver({
        method: 'POST', credentials: 'omit', ...(cssDiagnostic ? { keepalive: true } : {}),
        headers, body
      }, !!cssDiagnostic)
    } catch (_) { /* 관제 실패가 화면에 영향을 주지 않는다. */ }
  }
  const cssDiagnostics = installCssFailureDiagnostics(({ error, info, route, diagnostics }) => {
    transmit(error, 'javascript', info, undefined, diagnostics, route)
  })
  const report = (error, kind, info = '', original) => {
    if (!cssDiagnostics.capture(error, info)) transmit(error, kind, info, original)
  }
  activeReport = report
  const vueHandler = (error, instance, info) => {
    report(error, 'vue', info)
    if (previous) previous(error, instance, info)
  }
  const onError = event => {
    // CSS 리소스 오류는 별도의 캡처 리스너에서 수집한다.
    if (event.error || event.message) report(event.error || event.message, 'javascript')
  }
  const onRejection = event => report(event.reason, 'promise')
  app.config.errorHandler = vueHandler
  window.addEventListener('error', onError)
  window.addEventListener('unhandledrejection', onRejection)
  return () => {
    disposed = true
    pendingCss.forEach(item => clearTimeout(item.timer))
    pendingCss.length = 0
    window.removeEventListener('online', onOnline)
    cssDiagnostics.dispose()
    if (activeReport === report) activeReport = undefined
    if (app.config.errorHandler === vueHandler) app.config.errorHandler = previous
    window.removeEventListener('error', onError)
    window.removeEventListener('unhandledrejection', onRejection)
  }
}
